/**
 * settings-overlay.ts — Settings screen (v2M4).
 *
 * Tabbed full-screen overlay; v2M4 ships only the Gameplay tab. Other
 * tabs (Audio / Graphics / Controls / Accessibility / Interface per
 * UI_UX §10) land alongside their backing systems — gating principle:
 * no tab without real controls.
 *
 * Edit-then-Save model — the overlay holds a local working copy of the
 * settings; Save persists to storage, Cancel discards. Reachable from
 * both the main-menu Settings button and the in-game-menu Settings
 * button; same overlay either way.
 */

import {
  USER_SPEED_LADDER,
  type UserSpeed,
} from '../game/speed-controller';
import {
  type FontSizePreset,
  type GameplaySettings,
  type InterfaceSettings,
  type MinimapPosition,
  type Settings,
  type UiScalePreset,
  type VfxQualityPreset,
} from '../settings/types';
import { getAudioEngine } from '../audio';

export interface SettingsOverlayCallbacks {
  /** Current persisted settings (the overlay seeds its working copy from this). */
  current: Settings;
  /** Save was clicked. Caller persists and closes. */
  onSave(next: Settings): void;
  /** Cancel was clicked. Caller closes without persisting. */
  onCancel(): void;
}

export interface SettingsOverlayHandle {
  destroy(): void;
}

/**
 * Deep-clone a Settings record so the overlay's working copy is
 * isolated from the persisted one. Settings are flat enough that a
 * per-sub-object spread is sufficient.
 */
function cloneSettings(s: Settings): Settings {
  return {
    schemaVersion: s.schemaVersion,
    gameplay: { ...s.gameplay },
    audio: { ...s.audio },
    graphics: { ...s.graphics },
    controls: { ...s.controls },
    accessibility: { ...s.accessibility },
    // v4M1.5 — interface holds a `mutedAlertSources` array; clone it
    // explicitly so the working copy's mutations don't bleed into the
    // persisted record before Save.
    interface: {
      ...s.interface,
      mutedAlertSources: s.interface.mutedAlertSources.map((m) => ({ ...m })),
    },
  };
}

export function mountSettingsOverlay(
  parent: HTMLElement,
  callbacks: SettingsOverlayCallbacks,
): SettingsOverlayHandle {
  // Working copy. Never mutates callbacks.current; on Save, the working
  // copy is what we hand back.
  const working: Settings = cloneSettings(callbacks.current);

  const overlay = document.createElement('div');
  overlay.id = 'settings-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(5, 6, 10, 0.92)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1100',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#D7DEEA',
    // v4M1.11 — 150 ms opacity fade on mount + destroy (see in-game-menu.ts).
    opacity: '0',
    transition: 'opacity 150ms ease-out',
  } as Partial<CSSStyleDeclaration>);

  const card = document.createElement('div');
  Object.assign(card.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    minWidth: '520px',
    maxWidth: '640px',
    padding: '28px 32px',
    background: '#0E131C',
    border: '1px solid #2A3344',
    borderRadius: '2px',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(card);

  const heading = document.createElement('div');
  heading.textContent = 'SETTINGS';
  Object.assign(heading.style, {
    fontSize: '14px',
    letterSpacing: '0.18em',
    color: '#7E8799',
    marginBottom: '6px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(heading);

  // --- Tab strip --------------------------------------------------------
  // v4M1.5 — All six tabs land: Gameplay (v2M4), Audio (v2M8), Graphics
  // / Controls / Accessibility / Interface (v4M1.5). The tab registry
  // below replaces the previous two-tab hardcoded switch.
  const tabStrip = document.createElement('div');
  Object.assign(tabStrip.style, {
    display: 'flex',
    gap: '8px',
    borderBottom: '1px solid #2A3344',
    paddingBottom: '8px',
    marginBottom: '8px',
    flexWrap: 'wrap',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(tabStrip);

  type TabKey =
    | 'gameplay'
    | 'audio'
    | 'graphics'
    | 'controls'
    | 'accessibility'
    | 'interface';
  interface TabEntry {
    key: TabKey;
    label: string;
    tab: HTMLButtonElement;
    pane: HTMLElement;
  }
  const tabEntries: TabEntry[] = [];

  function addTab(key: TabKey, label: string): TabEntry {
    const active = tabEntries.length === 0; // first tab starts active
    const tab = makeTabButton(label, active);
    tabStrip.appendChild(tab);
    const pane = document.createElement('div');
    Object.assign(pane.style, {
      display: active ? 'flex' : 'none',
      flexDirection: 'column',
      gap: '14px',
      padding: '4px 0',
    } as Partial<CSSStyleDeclaration>);
    card.appendChild(pane);
    const entry: TabEntry = { key, label, tab, pane };
    tabEntries.push(entry);
    tab.addEventListener('click', () => switchTab(key));
    return entry;
  }

  function switchTab(which: TabKey): void {
    for (const e of tabEntries) {
      const isActive = e.key === which;
      e.pane.style.display = isActive ? 'flex' : 'none';
      setTabActive(e.tab, isActive);
    }
  }

  const gameplayPaneEntry = addTab('gameplay', 'Gameplay');
  const audioPaneEntry = addTab('audio', 'Audio');
  const graphicsPaneEntry = addTab('graphics', 'Graphics');
  const controlsPaneEntry = addTab('controls', 'Controls');
  const accessibilityPaneEntry = addTab('accessibility', 'Accessibility');
  const interfacePaneEntry = addTab('interface', 'Interface');

  const gameplayPane = gameplayPaneEntry.pane;
  const audioPane = audioPaneEntry.pane;
  const graphicsPane = graphicsPaneEntry.pane;
  const controlsPane = controlsPaneEntry.pane;
  const accessibilityPane = accessibilityPaneEntry.pane;
  const interfacePane = interfacePaneEntry.pane;

  // --- Gameplay pane ----------------------------------------------------
  gameplayPane.style.gap = '18px'; // gameplay tab inherited the chunkier spacing in v2M4
  gameplayPane.appendChild(makeSpeedRadioRow(working.gameplay));
  gameplayPane.appendChild(
    makeToggleRow(
      'Confirm Exit',
      'Show a confirmation prompt before quitting.',
      working.gameplay.confirmExit,
      (v) => {
        working.gameplay.confirmExit = v;
      },
    ),
  );
  gameplayPane.appendChild(
    makeToggleRow(
      'Confirm Return to Main Menu',
      'Show a confirmation prompt before discarding the current game.',
      working.gameplay.confirmReturnToMainMenu,
      (v) => {
        working.gameplay.confirmReturnToMainMenu = v;
      },
    ),
  );
  // v4M1.10 — Auto-pause on focus loss.
  gameplayPane.appendChild(
    makeToggleRow(
      'Pause when window loses focus',
      'Automatically pause the sim when the browser tab is switched away or minimised. Resumes when focus returns.',
      working.gameplay.pauseOnFocusLoss,
      (v) => {
        working.gameplay.pauseOnFocusLoss = v;
      },
    ),
  );
  // v4M1.4 — Autosave sub-section.
  gameplayPane.appendChild(makeSectionHeader('Autosave'));
  gameplayPane.appendChild(
    makeToggleRow(
      'Enable autosave',
      'Periodically auto-save the current game to the Autosave slot while the sim is running. The Autosave slot is read-only — manual saves still write to your chosen slot.',
      working.gameplay.autosaveEnabled,
      (v) => {
        working.gameplay.autosaveEnabled = v;
      },
    ),
  );
  gameplayPane.appendChild(
    makeAutosaveFrequencyRow(working.gameplay),
  );
  // v3M19.3 — Combat VFX sub-section. First entry is the floating-
  // damage-numbers toggle; future combat-VFX preferences (tracer
  // density, destruction-fade duration, etc.) land alongside.
  gameplayPane.appendChild(makeSectionHeader('Combat VFX'));
  gameplayPane.appendChild(
    makeToggleRow(
      'Show damage numbers',
      'Display floating damage numbers (and MISS tags) over ships during combat.',
      working.gameplay.showDamageNumbers,
      (v) => {
        working.gameplay.showDamageNumbers = v;
      },
    ),
  );

  // --- Audio pane (v2M8.8) ---------------------------------------------
  // Live-preview: each slider's onInput pushes the working volumes into
  // the engine immediately so the player can hear the change without
  // committing. Cancel restores the persisted values via the onCancel
  // path in main.ts (it doesn't touch ctx.settings, but the engine
  // would still hold the previewed values; that's acceptable for v1
  // since the next Settings open re-reads from persisted ctx.settings).
  const audioLivePreview = (): void => {
    getAudioEngine().applyVolumes(working.audio);
  };
  audioPane.appendChild(
    makeSliderRow('Master', working.audio.master, (v) => {
      working.audio.master = v;
      audioLivePreview();
    }),
  );
  audioPane.appendChild(
    makeSliderRow('Music', working.audio.music, (v) => {
      working.audio.music = v;
      audioLivePreview();
    }),
  );
  audioPane.appendChild(
    makeSliderRow('SFX', working.audio.sfx, (v) => {
      working.audio.sfx = v;
      audioLivePreview();
    }),
  );
  audioPane.appendChild(
    makeSliderRow('UI', working.audio.ui, (v) => {
      working.audio.ui = v;
      audioLivePreview();
    }),
  );

  // --- Graphics pane (v4M1.5) ------------------------------------------
  graphicsPane.appendChild(
    makePresetRow<UiScalePreset>(
      'UI scale',
      'Base font size for HUD + panel text. Applies on Save; takes full effect after the next session restart.',
      [
        { value: 'small', label: 'Small' },
        { value: 'default', label: 'Default' },
        { value: 'large', label: 'Large' },
      ],
      working.graphics.uiScale,
      (v) => {
        working.graphics.uiScale = v;
      },
    ),
  );
  graphicsPane.appendChild(
    makePresetRow<VfxQualityPreset>(
      'VFX quality',
      'Master quality preset for combat tracers + debris counts. Multiplies into per-frame VFX budgets.',
      [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
      ],
      working.graphics.vfxQuality,
      (v) => {
        working.graphics.vfxQuality = v;
      },
    ),
  );
  graphicsPane.appendChild(
    makeRangeRow(
      'Particle density',
      'Multiplier applied on top of VFX quality. 0.25× is the lightest; 2× is the densest. Default 1.0.',
      working.graphics.particleDensity,
      0.25,
      2.0,
      0.05,
      (v) => {
        working.graphics.particleDensity = v;
      },
    ),
  );

  // --- Controls pane (v4M1.5) ------------------------------------------
  controlsPane.appendChild(
    makeRangeRow(
      'Smooth-zoom speed',
      'Mouse-wheel zoom rate multiplier. 0.5× is slower (smoother); 2× is faster (snappier). Default 1.0.',
      working.controls.smoothZoomSpeed,
      0.5,
      2.0,
      0.1,
      (v) => {
        working.controls.smoothZoomSpeed = v;
      },
    ),
  );
  controlsPane.appendChild(
    makeToggleRow(
      'Hold G to preview galaxy view',
      'When on, the G key shows the galaxy view while held and returns to the prior view on release. When off, G toggles galaxy view on each press.',
      working.controls.holdGForGalaxyPreview,
      (v) => {
        working.controls.holdGForGalaxyPreview = v;
      },
    ),
  );
  controlsPane.appendChild(makeSectionHeader('Hotkey bindings'));
  controlsPane.appendChild(
    makeReadOnlyNote(
      'Hotkey rebinding is deferred to a future polish milestone. The default bindings are documented in UI_UX.md §11.',
    ),
  );

  // --- Accessibility pane (v4M1.5) -------------------------------------
  accessibilityPane.appendChild(
    makePresetRow<FontSizePreset>(
      'Font size',
      'Base text size for panels that opt into rem-based sizing. Applies on Save; takes full effect after the next session restart.',
      [
        { value: 'small', label: 'Small' },
        { value: 'default', label: 'Default' },
        { value: 'large', label: 'Large' },
      ],
      working.accessibility.fontSize,
      (v) => {
        working.accessibility.fontSize = v;
      },
    ),
  );
  accessibilityPane.appendChild(
    makeToggleRow(
      'Flash reduction',
      'Dampen animations that flash (alert-feed critical border, end-game stinger). Static highlight replaces the flash.',
      working.accessibility.flashReduction,
      (v) => {
        working.accessibility.flashReduction = v;
      },
    ),
  );
  accessibilityPane.appendChild(
    makeToggleRow(
      'Tooltip lingering',
      'Make tooltips persist until dismissed. Takes effect on the structured-tooltip widget shipping in a follow-on polish milestone; native browser tooltips are unaffected today.',
      working.accessibility.tooltipLingering,
      (v) => {
        working.accessibility.tooltipLingering = v;
      },
    ),
  );
  accessibilityPane.appendChild(makeSectionHeader('Colourblind modes'));
  accessibilityPane.appendChild(
    makeReadOnlyNote(
      'Colourblind modes are deferred to a future polish milestone. Render-side palette swap or LUT work pending.',
    ),
  );

  // --- Interface pane (v4M1.5) -----------------------------------------
  interfacePane.appendChild(
    makePresetRow<MinimapPosition>(
      'Minimap position',
      'Corner anchor for the mini-galaxy. Takes effect on the next session restart.',
      [
        { value: 'bottom-left', label: 'Bottom-left' },
        { value: 'bottom-right', label: 'Bottom-right' },
        { value: 'top-left', label: 'Top-left' },
        { value: 'top-right', label: 'Top-right' },
      ],
      working.interface.minimapPosition,
      (v) => {
        working.interface.minimapPosition = v;
      },
    ),
  );
  interfacePane.appendChild(
    makePresetRow<3 | 5 | 7>(
      'Alert feed length',
      'Maximum number of alert toasts visible simultaneously. Older alerts drop off when the cap is reached.',
      [
        { value: 3, label: '3' },
        { value: 5, label: '5' },
        { value: 7, label: '7' },
      ],
      working.interface.alertFeedMaxVisible,
      (v) => {
        working.interface.alertFeedMaxVisible = v;
      },
    ),
  );
  interfacePane.appendChild(
    makeToggleRow(
      'Auto-open detail panel on selection',
      'When on, clicking a planet or ship opens the detail panel automatically. When off, you must open the panel explicitly.',
      working.interface.autoOpenPanelsOnSelection,
      (v) => {
        working.interface.autoOpenPanelsOnSelection = v;
      },
    ),
  );
  interfacePane.appendChild(makeSectionHeader('Muted alert sources'));
  interfacePane.appendChild(
    makeMutedSourcesList(working.interface),
  );

  // --- Footer (Save / Cancel) ------------------------------------------
  const footer = document.createElement('div');
  Object.assign(footer.style, {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '14px',
    paddingTop: '14px',
    borderTop: '1px solid #2A3344',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(footer);

  const cancelBtn = makeFooterButton('Cancel', false);
  cancelBtn.addEventListener('click', () => {
    // v2M8.8 — revert any live-previewed audio changes by reapplying
    // the originals. Without this, a player who drags sliders then
    // clicks Cancel would keep hearing the preview until next session.
    getAudioEngine().applyVolumes(callbacks.current.audio);
    callbacks.onCancel();
  });
  footer.appendChild(cancelBtn);

  const saveBtn = makeFooterButton('Save', true);
  saveBtn.addEventListener('click', () => {
    callbacks.onSave(working);
  });
  footer.appendChild(saveBtn);

  parent.appendChild(overlay);
  // v4M1.11 — fade-in via rAF (see in-game-menu.ts for the rationale).
  requestAnimationFrame(() => {
    overlay.style.opacity = '1';
  });
  getAudioEngine().playCue('ui:panel-open');
  return {
    destroy(): void {
      getAudioEngine().playCue('ui:panel-close');
      overlay.style.opacity = '0';
      setTimeout(() => overlay.remove(), 150);
    },
  };
}

// --- Helpers ------------------------------------------------------------

function makeSpeedRadioRow(gameplay: GameplaySettings): HTMLElement {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  } as Partial<CSSStyleDeclaration>);

  const label = document.createElement('div');
  label.textContent = 'Default game speed';
  Object.assign(label.style, {
    fontSize: '13px',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(label);

  const help = document.createElement('div');
  help.textContent = 'Sim speed when a new or loaded game starts.';
  Object.assign(help.style, {
    fontSize: '11px',
    color: '#7E8799',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(help);

  const buttons = document.createElement('div');
  Object.assign(buttons.style, {
    display: 'inline-flex',
    gap: '0',
    alignSelf: 'flex-start',
    marginTop: '4px',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(buttons);

  const btnEls: HTMLButtonElement[] = USER_SPEED_LADDER.map((s, i) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = `${s}×`;
    btn.title = `Start every new game at ${s}× speed.`;
    Object.assign(btn.style, {
      padding: '6px 16px',
      background: '#0E131C',
      color: '#D7DEEA',
      border: '1px solid #2A3344',
      borderLeft: i === 0 ? '1px solid #2A3344' : 'none',
      fontFamily: 'inherit',
      fontSize: '12px',
      letterSpacing: '0.06em',
      cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);
    if (i === 0) {
      btn.style.borderTopLeftRadius = '2px';
      btn.style.borderBottomLeftRadius = '2px';
    }
    if (i === USER_SPEED_LADDER.length - 1) {
      btn.style.borderTopRightRadius = '2px';
      btn.style.borderBottomRightRadius = '2px';
    }
    return btn;
  });

  function paint(): void {
    for (let i = 0; i < btnEls.length; i++) {
      const btn = btnEls[i]!;
      const speed: UserSpeed = USER_SPEED_LADDER[i]!;
      const active = gameplay.defaultGameSpeed === speed;
      btn.style.background = active ? '#2B4A7A' : '#0E131C';
      btn.style.color = active ? '#FFFFFF' : '#D7DEEA';
      btn.style.borderColor = active ? '#3F6FA8' : '#2A3344';
      btn.style.borderLeft = i === 0 ? btn.style.borderColor : 'none';
    }
  }

  for (let i = 0; i < btnEls.length; i++) {
    const btn = btnEls[i]!;
    const speed: UserSpeed = USER_SPEED_LADDER[i]!;
    btn.addEventListener('click', () => {
      gameplay.defaultGameSpeed = speed;
      paint();
    });
    buttons.appendChild(btn);
  }
  paint();

  return row;
}

/**
 * v4M1.4 — autosave frequency picker. Three preset cadences keyed to
 * sim ticks (the sim runs at 10 Hz, so the second-equivalent at 1× is
 * `ticks / 10`). The presets cover the practical tuning band without
 * exposing a free-form slider: "Frequent" for short rollback windows,
 * "Default" for a quiet ~5 min cadence, "Rare" for minimal storage
 * churn on long games.
 */
function makeAutosaveFrequencyRow(gameplay: GameplaySettings): HTMLElement {
  const PRESETS: ReadonlyArray<{ label: string; ticks: number; help: string }> = [
    { label: 'Frequent', ticks: 1200, help: '~2 min at 1×' },
    { label: 'Default', ticks: 3000, help: '~5 min at 1×' },
    { label: 'Rare', ticks: 6000, help: '~10 min at 1×' },
  ];

  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  } as Partial<CSSStyleDeclaration>);

  const label = document.createElement('div');
  label.textContent = 'Autosave frequency';
  Object.assign(label.style, {
    fontSize: '13px',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(label);

  const help = document.createElement('div');
  help.textContent =
    'Cadence between autosaves while the sim is running. Tick-driven, so a paused game does not autosave.';
  Object.assign(help.style, {
    fontSize: '11px',
    color: '#7E8799',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(help);

  const buttons = document.createElement('div');
  Object.assign(buttons.style, {
    display: 'inline-flex',
    gap: '0',
    alignSelf: 'flex-start',
    marginTop: '4px',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(buttons);

  const btnEls: HTMLButtonElement[] = PRESETS.map((p, i) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = `${p.label} (${p.help})`;
    btn.title = `Autosave every ${p.ticks} sim ticks (${p.help}).`;
    Object.assign(btn.style, {
      padding: '6px 12px',
      background: '#0E131C',
      color: '#D7DEEA',
      border: '1px solid #2A3344',
      borderLeft: i === 0 ? '1px solid #2A3344' : 'none',
      fontFamily: 'inherit',
      fontSize: '12px',
      letterSpacing: '0.06em',
      cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);
    if (i === 0) {
      btn.style.borderTopLeftRadius = '2px';
      btn.style.borderBottomLeftRadius = '2px';
    }
    if (i === PRESETS.length - 1) {
      btn.style.borderTopRightRadius = '2px';
      btn.style.borderBottomRightRadius = '2px';
    }
    return btn;
  });

  function paint(): void {
    for (let i = 0; i < btnEls.length; i++) {
      const btn = btnEls[i]!;
      const active = gameplay.autosaveFrequencyTicks === PRESETS[i]!.ticks;
      btn.style.background = active ? '#2B4A7A' : '#0E131C';
      btn.style.color = active ? '#FFFFFF' : '#D7DEEA';
      btn.style.borderColor = active ? '#3F6FA8' : '#2A3344';
      btn.style.borderLeft = i === 0 ? btn.style.borderColor : 'none';
    }
  }

  for (let i = 0; i < btnEls.length; i++) {
    const btn = btnEls[i]!;
    const ticks = PRESETS[i]!.ticks;
    btn.addEventListener('click', () => {
      gameplay.autosaveFrequencyTicks = ticks;
      paint();
    });
    buttons.appendChild(btn);
  }
  paint();

  return row;
}

/**
 * v3M19.3 — sub-section divider for the Settings panes. Visually
 * partitions related controls under a labelled heading without
 * introducing a full nested-tab UX. Used by the Gameplay tab's
 * Combat VFX section; can be reused by other tabs as their control
 * counts grow.
 */
function makeSectionHeader(label: string): HTMLElement {
  const wrap = document.createElement('div');
  Object.assign(wrap.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '4px',
    paddingTop: '12px',
    borderTop: '1px solid #2A3344',
  } as Partial<CSSStyleDeclaration>);
  const text = document.createElement('div');
  text.textContent = label;
  Object.assign(text.style, {
    fontSize: '11px',
    letterSpacing: '0.10em',
    color: '#8EA0C8',
    textTransform: 'uppercase',
  } as Partial<CSSStyleDeclaration>);
  wrap.appendChild(text);
  return wrap;
}

function makeToggleRow(
  label: string,
  help: string,
  initial: boolean,
  onChange: (v: boolean) => void,
): HTMLElement {
  let value = initial;
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  } as Partial<CSSStyleDeclaration>);

  const top = document.createElement('div');
  Object.assign(top.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(top);

  const labelEl = document.createElement('div');
  labelEl.textContent = label;
  Object.assign(labelEl.style, {
    flex: '1 1 auto',
    fontSize: '13px',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);
  top.appendChild(labelEl);

  const toggleBtn = document.createElement('button');
  toggleBtn.type = 'button';
  toggleBtn.title = `${label} — click to toggle. ${help}`;
  Object.assign(toggleBtn.style, {
    padding: '6px 14px',
    fontFamily: 'inherit',
    fontSize: '12px',
    letterSpacing: '0.06em',
    border: '1px solid',
    borderRadius: '2px',
    cursor: 'pointer',
    minWidth: '64px',
    // v4M1.11 — smooth On/Off colour ramp instead of an instant snap.
    transition: 'background 100ms ease-out, border-color 100ms ease-out, color 100ms ease-out',
  } as Partial<CSSStyleDeclaration>);

  function paint(): void {
    toggleBtn.textContent = value ? 'On' : 'Off';
    toggleBtn.style.background = value ? '#2B4A7A' : '#0E131C';
    toggleBtn.style.color = value ? '#FFFFFF' : '#D7DEEA';
    toggleBtn.style.borderColor = value ? '#3F6FA8' : '#2A3344';
  }
  toggleBtn.addEventListener('click', () => {
    value = !value;
    onChange(value);
    paint();
  });
  paint();
  top.appendChild(toggleBtn);

  const helpEl = document.createElement('div');
  helpEl.textContent = help;
  Object.assign(helpEl.style, {
    fontSize: '11px',
    color: '#7E8799',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(helpEl);

  return row;
}

function makeFooterButton(label: string, primary: boolean): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  btn.title = primary
    ? 'Persist these settings and close.'
    : 'Discard changes and close.';
  const baseBg = primary ? '#2B4A7A' : '#0E131C';
  const baseBorder = primary ? '#3F6FA8' : '#2A3344';
  const hoverBg = primary ? '#365D96' : '#172031';
  const hoverBorder = primary ? '#5A87C0' : '#3F4C66';
  Object.assign(btn.style, {
    padding: '10px 24px',
    background: baseBg,
    color: primary ? '#FFFFFF' : '#D7DEEA',
    border: `1px solid ${baseBorder}`,
    borderRadius: '2px',
    fontFamily: 'inherit',
    fontSize: '13px',
    letterSpacing: '0.06em',
    cursor: 'pointer',
    // v4M1.11 — hover lift on the Save / Cancel footer.
    transition: 'background 100ms ease-out, border-color 100ms ease-out',
  } as Partial<CSSStyleDeclaration>);
  btn.addEventListener('mouseenter', () => {
    btn.style.background = hoverBg;
    btn.style.borderColor = hoverBorder;
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.background = baseBg;
    btn.style.borderColor = baseBorder;
  });
  return btn;
}

/**
 * v2M8.8 — clickable tab pill. Active state matches the previous
 * single-tab inline styling so the visual didn't shift when Audio
 * joined.
 */
function makeTabButton(label: string, active: boolean): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  btn.title = `Switch to the ${label} settings tab.`;
  Object.assign(btn.style, {
    padding: '6px 12px',
    borderRadius: '2px',
    fontFamily: 'inherit',
    fontSize: '12px',
    letterSpacing: '0.06em',
    cursor: 'pointer',
  } as Partial<CSSStyleDeclaration>);
  setTabActive(btn, active);
  return btn;
}

function setTabActive(btn: HTMLButtonElement, active: boolean): void {
  btn.style.background = active ? '#172031' : '#0E131C';
  btn.style.border = `1px solid ${active ? '#3F4C66' : '#2A3344'}`;
  btn.style.color = active ? '#D7DEEA' : '#9FA5B3';
}

/**
 * v2M8.8 — 0..1 volume slider row. Live-callback fires on every
 * input event so the player hears the change immediately (the
 * caller calls `getAudioEngine().applyVolumes` in `onChange`).
 */
function makeSliderRow(
  label: string,
  initial: number,
  onChange: (value: number) => void,
): HTMLElement {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  } as Partial<CSSStyleDeclaration>);

  const lab = document.createElement('div');
  lab.textContent = label;
  Object.assign(lab.style, {
    width: '70px',
    fontSize: '13px',
    letterSpacing: '0.04em',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(lab);

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '0';
  slider.max = '100';
  slider.step = '1';
  slider.value = String(Math.round(initial * 100));
  slider.style.flex = '1';
  slider.dataset.noSound = ''; // suppress global ui:click on slider drag
  slider.title = `${label} volume — 0 mutes the channel; 100 is full volume.`;
  row.appendChild(slider);

  const readout = document.createElement('div');
  readout.textContent = `${slider.value}`;
  Object.assign(readout.style, {
    width: '36px',
    textAlign: 'right',
    fontSize: '12px',
    color: '#7E8799',
    fontVariantNumeric: 'tabular-nums',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(readout);

  slider.addEventListener('input', () => {
    const v = Number(slider.value) / 100;
    readout.textContent = slider.value;
    onChange(v);
  });

  return row;
}

/**
 * v4M1.5 — generic preset-button row. Renders a labelled segmented
 * control. The active preset is highlighted; clicking another preset
 * mutates the working settings via `onChange`. Used by Graphics
 * (UI scale, VFX quality), Accessibility (Font size), and Interface
 * (Minimap position, Alert feed length).
 */
function makePresetRow<T extends string | number>(
  label: string,
  help: string,
  presets: ReadonlyArray<{ value: T; label: string }>,
  initial: T,
  onChange: (value: T) => void,
): HTMLElement {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  } as Partial<CSSStyleDeclaration>);

  const labelEl = document.createElement('div');
  labelEl.textContent = label;
  Object.assign(labelEl.style, {
    fontSize: '13px',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(labelEl);

  const helpEl = document.createElement('div');
  helpEl.textContent = help;
  Object.assign(helpEl.style, {
    fontSize: '11px',
    color: '#7E8799',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(helpEl);

  const buttons = document.createElement('div');
  Object.assign(buttons.style, {
    display: 'inline-flex',
    gap: '0',
    alignSelf: 'flex-start',
    marginTop: '4px',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(buttons);

  let current: T = initial;
  const btnEls: HTMLButtonElement[] = presets.map((p, i) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = p.label;
    btn.title = `${label}: ${p.label}`;
    Object.assign(btn.style, {
      padding: '6px 14px',
      background: '#0E131C',
      color: '#D7DEEA',
      border: '1px solid #2A3344',
      borderLeft: i === 0 ? '1px solid #2A3344' : 'none',
      fontFamily: 'inherit',
      fontSize: '12px',
      letterSpacing: '0.06em',
      cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);
    if (i === 0) {
      btn.style.borderTopLeftRadius = '2px';
      btn.style.borderBottomLeftRadius = '2px';
    }
    if (i === presets.length - 1) {
      btn.style.borderTopRightRadius = '2px';
      btn.style.borderBottomRightRadius = '2px';
    }
    return btn;
  });

  function paint(): void {
    for (let i = 0; i < btnEls.length; i++) {
      const btn = btnEls[i]!;
      const active = current === presets[i]!.value;
      btn.style.background = active ? '#2B4A7A' : '#0E131C';
      btn.style.color = active ? '#FFFFFF' : '#D7DEEA';
      btn.style.borderColor = active ? '#3F6FA8' : '#2A3344';
      btn.style.borderLeft = i === 0 ? btn.style.borderColor : 'none';
    }
  }

  for (let i = 0; i < btnEls.length; i++) {
    const btn = btnEls[i]!;
    const value = presets[i]!.value;
    btn.addEventListener('click', () => {
      current = value;
      onChange(value);
      paint();
    });
    buttons.appendChild(btn);
  }
  paint();
  return row;
}

/**
 * v4M1.5 — labelled numeric-range slider with help text + live readout.
 * Distinct from `makeSliderRow` (which is 0..1 normalised for the audio
 * volume sliders) in that it accepts an arbitrary min/max/step and
 * displays the raw value with up to two decimals.
 */
function makeRangeRow(
  label: string,
  help: string,
  initial: number,
  min: number,
  max: number,
  step: number,
  onChange: (value: number) => void,
): HTMLElement {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  } as Partial<CSSStyleDeclaration>);

  const labelEl = document.createElement('div');
  labelEl.textContent = label;
  Object.assign(labelEl.style, {
    fontSize: '13px',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(labelEl);

  const helpEl = document.createElement('div');
  helpEl.textContent = help;
  Object.assign(helpEl.style, {
    fontSize: '11px',
    color: '#7E8799',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(helpEl);

  const sliderRow = document.createElement('div');
  Object.assign(sliderRow.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(sliderRow);

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = String(min);
  slider.max = String(max);
  slider.step = String(step);
  slider.value = String(initial);
  slider.style.flex = '1';
  slider.title = `${label} — drag to adjust between ${min}× and ${max}×.`;
  sliderRow.appendChild(slider);

  const readout = document.createElement('div');
  readout.textContent = formatRangeValue(initial);
  Object.assign(readout.style, {
    width: '52px',
    textAlign: 'right',
    fontSize: '12px',
    color: '#7E8799',
    fontVariantNumeric: 'tabular-nums',
  } as Partial<CSSStyleDeclaration>);
  sliderRow.appendChild(readout);

  slider.addEventListener('input', () => {
    const v = Number(slider.value);
    readout.textContent = formatRangeValue(v);
    onChange(v);
  });
  return row;
}

function formatRangeValue(v: number): string {
  // Two decimals for sub-1, one decimal otherwise. Avoids "1.00" when
  // the slider lands on a tidy value.
  if (Math.abs(v - Math.round(v)) < 0.001) return `${Math.round(v)}.0×`;
  return `${v.toFixed(2)}×`;
}

/**
 * v4M1.5 — italicised note for deferred-feature stubs in the settings
 * panes. Used by Controls (hotkey rebinding) and Accessibility
 * (colourblind modes) to surface why a documented feature isn't yet
 * configurable in the UI.
 */
function makeReadOnlyNote(text: string): HTMLElement {
  const note = document.createElement('div');
  note.textContent = text;
  Object.assign(note.style, {
    fontSize: '12px',
    color: '#7E8799',
    fontStyle: 'italic',
    lineHeight: '1.5',
  } as Partial<CSSStyleDeclaration>);
  return note;
}

/**
 * v4M1.5 — muted-alert-sources list (read-only view + un-mute).
 * Right-click-to-mute on toasts is a follow-on item; for now the list
 * is empty by default and only re-populates if some other code path
 * appends to `interface.mutedAlertSources`. The un-mute button per
 * entry covers the "got stuck with a stale mute" recovery case.
 */
function makeMutedSourcesList(iface: InterfaceSettings): HTMLElement {
  const container = document.createElement('div');
  Object.assign(container.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  } as Partial<CSSStyleDeclaration>);

  function render(): void {
    container.innerHTML = '';
    if (iface.mutedAlertSources.length === 0) {
      const empty = document.createElement('div');
      empty.textContent =
        'No muted sources. Right-click a toast in-game to mute that source — landing in a future polish pass.';
      Object.assign(empty.style, {
        fontSize: '12px',
        color: '#7E8799',
        fontStyle: 'italic',
      } as Partial<CSSStyleDeclaration>);
      container.appendChild(empty);
      return;
    }
    for (let i = 0; i < iface.mutedAlertSources.length; i++) {
      const src = iface.mutedAlertSources[i]!;
      const row = document.createElement('div');
      Object.assign(row.style, {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '4px 6px',
        background: '#05060A',
        border: '1px solid #2A3344',
        borderRadius: '2px',
      } as Partial<CSSStyleDeclaration>);

      const label = document.createElement('span');
      label.textContent = `${src.kind} #${src.id}`;
      Object.assign(label.style, {
        fontSize: '12px',
        color: '#D7DEEA',
        fontVariantNumeric: 'tabular-nums',
      } as Partial<CSSStyleDeclaration>);
      row.appendChild(label);

      const unmute = document.createElement('button');
      unmute.type = 'button';
      unmute.textContent = 'Un-mute';
      unmute.title = 'Remove this source from the muted list.';
      Object.assign(unmute.style, {
        padding: '4px 10px',
        background: 'transparent',
        color: '#8ea0c8',
        border: '1px solid #2A3344',
        borderRadius: '2px',
        fontFamily: 'inherit',
        fontSize: '11px',
        cursor: 'pointer',
      } as Partial<CSSStyleDeclaration>);
      const indexToRemove = i;
      unmute.addEventListener('click', () => {
        iface.mutedAlertSources.splice(indexToRemove, 1);
        render();
      });
      row.appendChild(unmute);

      container.appendChild(row);
    }
  }

  render();
  return container;
}
